import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Mic, Square, ArrowUp, Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePanel } from "@/context/PanelContext";
import { avatarFor } from "@/lib/utils-tg";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = [
  "I worked 2h on onboarding",
  "Create a task: fix pricing page",
  "What did I do today?",
  "Need approval for $200 software",
];

const CREATED_LABELS = {
  activity: "Time logged",
  task: "Task created",
  goal: "Goal created",
  meeting: "Meeting created",
  issue: "Issue raised",
  approval: "Approval requested",
};
function createdLabel(type) {
  return CREATED_LABELS[type] || `${(type || "Item").charAt(0).toUpperCase() + (type || "").slice(1)} created`;
}

export default function AIChat() {
  const { user } = useAuth();
  const { triggerRefresh } = usePanel();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]); // {role: 'user'|'ai', text, intent?, created?}
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [history.length, busy, transcribing]);

  const send = async (text) => {
    const msg = (text ?? value).trim();
    if (!msg || busy) return;
    setHistory((h) => [...h, { role: "user", text: msg }]);
    setValue("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg });
      setHistory((h) => [
        ...h,
        { role: "ai", text: data.reply || "Done.", intent: data.intent, created: data.created },
      ]);
      triggerRefresh();
      if (data.created) {
        toast.success(createdLabel(data.created.type));
      }
    } catch (e) {
      const msgErr = e?.response?.data?.detail || "AI is unavailable.";
      setHistory((h) => [...h, { role: "ai", text: msgErr, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: pickMime() });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (blob.size < 800) {
          toast("Recording too short");
          return;
        }
        await transcribeAndSend(blob);
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    const mr = recorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  };

  const transcribeAndSend = async (blob) => {
    setTranscribing(true);
    try {
      const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
      const fd = new FormData();
      fd.append("file", blob, `voice.${ext}`);
      const { data } = await api.post("/ai/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data.text || "").trim();
      if (!text) {
        toast("I didn't catch that. Try again.");
        return;
      }
      await send(text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div
      className="hidden md:flex w-96 fixed right-0 top-0 bottom-0 bg-white border-l border-slate-200 z-30 flex-col"
      data-testid="ai-chat-panel"
    >
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="tg-stat-icon h-9 w-9 bg-emerald-50">
            <Sparkles className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900 text-base">AI Assistant</div>
            <div className="text-xs text-slate-500">Talk to organize your work</div>
          </div>
        </div>
        {history.length > 0 && (
          <button
            type="button"
            onClick={() => setHistory([])}
            className="text-xs text-slate-400 hover:text-slate-600"
            data-testid="ai-clear"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {history.length === 0 && (
          <div className="space-y-3">
            <div className="text-sm text-slate-600 bg-emerald-50/60 border border-emerald-100 rounded-2xl p-4 leading-relaxed">
              Hey {user?.name?.split(" ")[0] || "there"} — speak or type. I'll create tasks,
              log time, raise issues, request approvals, or summarise your day.
            </div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => send(s)}
                  disabled={busy}
                  className="text-xs text-slate-600 hover:text-emerald-600 bg-white hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 rounded-full px-3 py-1.5 transition-all"
                  data-testid={`ai-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {history.map((m, i) => {
          if (m.role === "user") {
            return (
              <div key={i} className="flex gap-2 flex-row-reverse" data-testid="ai-msg-user">
                <img
                  src={user?.avatar_url || avatarFor(user?.name || "U")}
                  alt=""
                  className="h-7 w-7 rounded-full object-cover shrink-0"
                />
                <div className="max-w-[80%] bg-[#16A34A] text-white rounded-2xl rounded-br-md px-3.5 py-2 text-sm leading-relaxed">
                  {m.text}
                </div>
              </div>
            );
          }
          return (
            <div key={i} className="flex gap-2" data-testid="ai-msg-ai">
              <div className="h-7 w-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <div className={`max-w-[85%] rounded-2xl rounded-bl-md px-3.5 py-2 text-sm leading-relaxed whitespace-pre-line ${
                m.error
                  ? "bg-rose-50 text-rose-700 border border-rose-100"
                  : "bg-slate-100 text-slate-800"
              }`}>
                {m.text}
                {m.created && (
                  <div className="mt-1.5 inline-flex items-center gap-1 tg-pill bg-white text-emerald-700 border-emerald-200">
                    <Plus className="h-3 w-3" /> {createdLabel(m.created.type)}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {(busy || transcribing) && (
          <div className="flex gap-2" data-testid="ai-thinking">
            <div className="h-7 w-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="bg-slate-100 rounded-2xl rounded-bl-md px-3.5 py-2 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {transcribing ? "Transcribing…" : "Thinking…"}
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-slate-200 bg-white">
        {recording && (
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-rose-600" data-testid="recording-indicator">
            <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
            Recording — click stop when done
          </div>
        )}
        <div className="flex items-center gap-2 bg-slate-50 rounded-2xl pl-3 pr-1 py-1 border border-slate-200 focus-within:border-emerald-300 transition-colors">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={recording ? "Listening…" : "Ask anything about your work…"}
            disabled={busy || recording || transcribing}
            className="flex-1 bg-transparent border-0 focus:ring-0 focus:outline-none text-sm py-2 placeholder:text-slate-400"
            data-testid="ai-input"
          />
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={busy || transcribing}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-all ${
              recording
                ? "bg-rose-500 hover:bg-rose-600 text-white"
                : "bg-emerald-50 hover:bg-emerald-100 text-emerald-600"
            }`}
            title={recording ? "Stop & transcribe" : "Voice input"}
            data-testid="ai-mic-button"
          >
            {recording ? <Square className="h-3.5 w-3.5" fill="currentColor" /> : <Mic className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => send()}
            disabled={busy || recording || transcribing || !value.trim()}
            className="h-9 w-9 rounded-full bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-40 text-white flex items-center justify-center transition-all"
            data-testid="ai-send-button"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function pickMime() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(c)) return c;
  }
  return "";
}
